import { IPoint2d } from '@/editor/math/IPoint2d';
import { Point2dUtils } from '@/editor/math/Point2dUtils';
import { Ray2d } from '@/editor/math/ray2d';
import { globalStores } from '../..';
import { LineSeg2d } from '@/editor/math/LineSeg2d';
import { intersectCommonShape } from '@/editor/math/shape/intesection';
import { IPureEdge } from '@/editor/types';
import { ArcSeg2d } from '@/editor/math/arcSeg2d';
import type { IShapePoint2d } from '@/editor/types/shape';
import { IRulerInfo } from '@/editor/entities/2d/rulerModel';
import { isArc } from '@/editor/utils/arc';

export interface IBoxRulerInfo extends IRulerInfo {
  start: IPoint2d;
  end: IPoint2d;
  rayDirection: IPoint2d;
  /** box 标尺画在被测线段上，不走墙体标尺那套 DEFAULT_RULER_OFFSET 偏移，故恒为 0 */
  offset: number;
}

function shapeToEdges(shape: IShapePoint2d[], closed: boolean): IPureEdge[] {
  if (shape.length < 2) return [];

  const isAlreadyClosed = Point2dUtils.equals(shape[0], shape[shape.length - 1]);
  const edgeCount = closed ? (isAlreadyClosed ? shape.length - 1 : shape.length) : shape.length - 1;
  const edges: IPureEdge[] = [];

  for (let i = 0; i < edgeCount; i++) {
    const start = shape[i];
    const end = shape[(i + 1) % shape.length];
    if (Point2dUtils.equals(start, end)) continue;

    const edge: IPureEdge = { start, end };
    if (start.center) {
      edge.center = start.center;
      edge.clockwise = start.clockwise;
    }

    edges.push(edge);
  }

  return edges;
}

export function calculateBoxRulerInfos(
  boxData: {
    center: IPoint2d;
    width: number;
    thickness: number;
    rotation: number;
  },
  extraLines: IPureEdge[] = [],
  noIncludeSepWalls?: boolean
): IBoxRulerInfo[] {
  const { center, width, thickness, rotation } = boxData;
  const horizontalDirection = Point2dUtils.rotateDirection({ x: 1, y: 0 }, rotation);
  const verticalDirection = Point2dUtils.rotateDirection({ x: 0, y: 1 }, rotation);

  const leftRayOrigin = Point2dUtils.addDistance(center, -width / 2, horizontalDirection);
  const rightRayOrigin = Point2dUtils.addDistance(center, width / 2, horizontalDirection);
  const topRayOrigin = Point2dUtils.addDistance(center, thickness / 2, verticalDirection);
  const bottomRayOrigin = Point2dUtils.addDistance(center, -thickness / 2, verticalDirection);

  const leftRay = new Ray2d(leftRayOrigin, { x: -horizontalDirection.x, y: -horizontalDirection.y });
  const rightRay = new Ray2d(rightRayOrigin, horizontalDirection);
  const topRay = new Ray2d(topRayOrigin, verticalDirection);
  const bottomRay = new Ray2d(bottomRayOrigin, { x: -verticalDirection.x, y: -verticalDirection.y });

  // 查找射线最近点
  let leftRayMinDistance = Infinity;
  let leftIntersect = null;
  let rightRayMinDistance = Infinity;
  let rightIntersect = null;
  let topRayMinDistance = Infinity;
  let topIntersect = null;
  let bottomRayMinDistance = Infinity;
  let bottomIntersect = null;

  let edges: IPureEdge[] = globalStores.connectedWallOutlines.flatMap((outline) => outline.edges);
  edges = edges.concat(extraLines);

  if (!noIncludeSepWalls) {
    const separateWallEdges = globalStores.separateWallStore
      .getSepDatas({ onlyNotBase: true })
      .flatMap((sepWallData) => shapeToEdges(sepWallData.drawSeparateWallInfo.drawShape, sepWallData.thickness > 0));
    edges = edges.concat(separateWallEdges);
  }
  for (const edge of edges) {
    const seg = isArc(edge)
      ? new ArcSeg2d({ start: edge.start, end: edge.end, center: edge.center, clockwise: edge.clockwise })
      : new LineSeg2d(edge.start, edge.end);

    let intersectPoints = intersectCommonShape(leftRay, seg);
    let intersectPoint = intersectPoints?.[0];
    if (intersectPoint) {
      const distance = Point2dUtils.distanceTo(leftRayOrigin, intersectPoint);
      if (distance < leftRayMinDistance) {
        leftRayMinDistance = distance;
        leftIntersect = intersectPoint;
      }
    }

    intersectPoints = intersectCommonShape(rightRay, seg);
    intersectPoint = intersectPoints?.[0];
    if (intersectPoint) {
      const distance = Point2dUtils.distanceTo(rightRayOrigin, intersectPoint);
      if (distance < rightRayMinDistance) {
        rightRayMinDistance = distance;
        rightIntersect = intersectPoint;
      }
    }

    intersectPoints = intersectCommonShape(topRay, seg);
    intersectPoint = intersectPoints?.[0];
    if (intersectPoint) {
      const distance = Point2dUtils.distanceTo(topRayOrigin, intersectPoint);
      if (distance < topRayMinDistance) {
        topRayMinDistance = distance;
        topIntersect = intersectPoint;
      }
    }

    intersectPoints = intersectCommonShape(bottomRay, seg);
    intersectPoint = intersectPoints?.[0];
    if (intersectPoint) {
      const distance = Point2dUtils.distanceTo(bottomRayOrigin, intersectPoint);
      if (distance < bottomRayMinDistance) {
        bottomRayMinDistance = distance;
        bottomIntersect = intersectPoint;
      }
    }
  }

  const rulerInfos: IBoxRulerInfo[] = [];
  // 交点存在与「最近距离已被刷新」是同一件事，直接判交点即可收窄类型
  if (leftIntersect) {
    rulerInfos.push({
      id: 'l',
      start: leftRayOrigin,
      end: leftIntersect,
      rayDirection: leftRay.direction,
      offset: 0
    });
  }
  if (rightIntersect) {
    rulerInfos.push({
      id: 'r',
      start: rightRayOrigin,
      end: rightIntersect,
      rayDirection: rightRay.direction,
      offset: 0
    });
  }
  if (topIntersect) {
    rulerInfos.push({
      id: 't',
      start: topRayOrigin,
      end: topIntersect,
      rayDirection: topRay.direction,
      offset: 0
    });
  }
  if (bottomIntersect) {
    rulerInfos.push({
      id: 'b',
      start: bottomRayOrigin,
      end: bottomIntersect,
      rayDirection: bottomRay.direction,
      offset: 0
    });
  }

  return rulerInfos;
}
